import { ApiRequestError } from '../api';
import { getLiveStreams } from '../app';
import { isAbort, loadPlayer } from '../data';
import { loc, t } from '../i18n';
import { goBack, setTitle } from '../router';
import type { Player } from '../types';
import { $, errorState, h, link, loadingState, richText, svg } from '../ui/dom';
import { raceBadge, raceLabel, roleLabel, rosterRange } from '../ui/cards';
import { icons } from '../ui/icons';

let controller: AbortController | null = null;

function fact(label: string, value: Node | string): HTMLElement {
  return h('div', 'fact', h('dt', { class: 'mono', text: label }), h('dd', null, value));
}

export async function mountPlayer(params: URLSearchParams): Promise<void> {
  controller?.abort();
  controller = new AbortController();
  const signal = controller.signal;
  const root = $('player-card');
  if (!root) return;
  const id = Number(params.get('id'));
  const back = h(
    'button',
    { class: 'back-link mono', attrs: { type: 'button' }, on: { click: () => goBack('/roster') } },
    svg(icons.arrowLeft),
    t('player.toRoster'),
  );

  if (!Number.isInteger(id) || id <= 0) {
    root.replaceChildren(back, h('div', 'state state-empty', t('player.notFound')));
    return;
  }
  root.replaceChildren(back, loadingState());

  let p: Player;
  try {
    p = await loadPlayer(id, signal);
  } catch (err) {
    if (isAbort(err)) return;
    root.replaceChildren(
      back,
      err instanceof ApiRequestError && err.status === 404
        ? h('div', 'state state-empty', t('player.notFound'))
        : errorState(() => void mountPlayer(params)),
    );
    return;
  }
  if (signal.aborted) return;
  setTitle(p.nickname);

  const live = getLiveStreams().find((s) => s.player_id === p.id);
  const bio = loc(p, 'bio');

  root.replaceChildren(
    back,
    h(
      'article',
      `player-profile race-theme-${p.race}`,
      h(
        'div',
        'player-visual',
        p.avatar_url
          ? h('img', { class: 'player-avatar', attrs: { src: p.avatar_url, alt: p.nickname } })
          : h('div', 'player-avatar player-avatar-ph', h('span', null, p.nickname.charAt(0).toUpperCase())),
        h('span', 'player-race-big', raceBadge(p.race, true)),
      ),
      h(
        'div',
        'player-info',
        h('div', 'tech-line', h('span', { class: 'mono', text: `${t('player.card')} · ID ${String(p.id).padStart(4, '0')}` }), h('span', 'rule')),
        h('h1', 'player-name', p.nickname),
        h('span', { class: `role-badge role-${p.role}`, text: roleLabel(p.role) }),
        live ? link(live.url, 'live-pill', h('span', 'live-dot'), `${t('live.title')} — ${t('live.watch')}`) : null,
        h(
          'dl',
          'facts',
          fact(t('player.race'), raceLabel(p.race)),
          fact(t('player.role'), roleLabel(p.role)),
          fact(t('player.mmr'), p.mmr ? String(p.mmr) : '—'),
          fact(
            t('player.roster'),
            p.roster ? link(`/roster#roster-${p.roster.id}`, 'inline-link', `${p.roster.name} · ${rosterRange(p.roster)}`) : '—',
          ),
          p.battle_tag ? fact(t('player.battleTag'), p.battle_tag) : null,
          p.country ? fact(t('player.country'), p.country) : null,
          fact(t('player.pulse'), p.pulse_url ? link(p.pulse_url, 'inline-link', t('player.openPulse'), svg(icons.external)) : '—'),
        ),
        bio ? richText(bio, 'rich player-bio') : null,
        h(
          'div',
          'player-links',
          p.pulse_url ? link(p.pulse_url, 'btn btn-primary btn-sm', 'SC2 Pulse', svg(icons.external)) : null,
          p.twitch_url ? link(p.twitch_url, 'btn btn-ghost btn-sm', svg(icons.twitch), 'Twitch') : null,
          p.youtube_url ? link(p.youtube_url, 'btn btn-ghost btn-sm', svg(icons.youtube), 'YouTube') : null,
        ),
      ),
    ),
  );
}

export function unmountPlayer(): void {
  controller?.abort();
  controller = null;
}
